import React from 'react';
import { Link } from 'react-router-dom';
import { AlertCircle, ChevronRight } from 'lucide-react';

// Inline clause-section editor. Rendered inside the open accordion panel
// when editingClauseNum matches. Keyboard: Esc cancels, Cmd/Ctrl+Enter saves.
export function ClauseEditForm({ clause, draft, setDraft, saving, error, onSave, onCancel }) {
  const keydown = (e) => {
    if (e.key === 'Escape') { e.preventDefault(); onCancel(); }
    else if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') { e.preventDefault(); onSave(); }
  };
  const set = (key, val) => setDraft((prev) => ({ ...prev, [key]: val }));

  return (
    <div onKeyDown={keydown}>
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'minmax(160px, max-content) 1fr',
        gap: '14px 40px',
        alignItems: 'start',
      }}>
        {clause.fields.map((fd) => {
          const v = draft[fd.key] ?? '';
          const inputId = `clause-${clause.num}-${fd.key}`;
          return (
            <React.Fragment key={fd.key}>
              <label htmlFor={inputId} className="tertiary" style={{ fontSize: 12, paddingTop: 9 }}>{fd.label}</label>
              <div>
                {fd.type === 'textarea' ? (
                  <textarea
                    id={inputId}
                    className="textarea"
                    rows={Math.min(8, Math.max(2, Math.ceil((v.length || 0) / 80) + 1))}
                    value={v}
                    onChange={(e) => set(fd.key, e.target.value)}
                    style={{ width: '100%', fontSize: 13, lineHeight: 1.55 }}
                  />
                ) : fd.type === 'select' ? (
                  <select
                    id={inputId}
                    className="select"
                    value={v}
                    onChange={(e) => set(fd.key, e.target.value)}
                    style={{ width: '100%', fontSize: 13 }}
                  >
                    {fd.options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                  </select>
                ) : (
                  <input
                    id={inputId}
                    type={fd.type === 'date' ? 'date' : fd.type === 'number' ? 'number' : 'text'}
                    className="input"
                    value={v}
                    onChange={(e) => set(fd.key, e.target.value)}
                    style={{ width: fd.type === 'date' || fd.type === 'number' ? 220 : '100%', fontSize: 13 }}
                    step={fd.type === 'number' ? 'any' : undefined}
                  />
                )}
              </div>
            </React.Fragment>
          );
        })}
      </div>
      {error && (
        <div className="callout red" style={{ marginTop: 14 }}>
          <div className="callout-icon"><AlertCircle className="w-4 h-4" /></div>
          <div className="callout-body">{error}</div>
        </div>
      )}
      <div className="row" style={{ marginTop: 20, gap: 8 }}>
        <button className="btn primary sm" onClick={onSave} disabled={saving}>
          {saving ? 'Saving…' : 'Save changes'}
        </button>
        <button className="btn ghost sm" onClick={onCancel} disabled={saving}>Cancel</button>
        <span className="muted mono" style={{ fontSize: 11, marginLeft: 'auto' }}>
          ⌘↵ save · esc cancel
        </span>
      </div>
    </div>
  );
}

export function MenuItem({ icon: Icon, label, tone, onClick }) {
  const color =
    tone === 'red' ? 'var(--text-critical)' :
    tone === 'yellow' ? 'var(--text-yellow)' :
    'var(--text-primary)';
  return (
    <button
      onClick={onClick}
      style={{
        width: '100%',
        display: 'flex', alignItems: 'center', gap: 10,
        padding: '8px 10px',
        borderRadius: 6,
        fontSize: 13,
        color,
        textAlign: 'left',
        transition: 'background 120ms',
      }}
      onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--surface-hover)'; }}
      onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
    >
      <Icon className="w-3.5 h-3.5" style={{ color, flexShrink: 0 }} />
      {label}
    </button>
  );
}

export function PersonRow({ name, role, sub }) {
  const initials = (name || '?').split(' ').map((p) => p[0]).slice(0, 2).join('').toUpperCase();
  return (
    <div className="row" style={{ gap: 10 }}>
      <span className="avatar" style={{ width: 30, height: 30, fontSize: 11 }}>{initials}</span>
      <div style={{ minWidth: 0, flex: 1 }}>
        <div style={{ fontSize: 13, fontWeight: 450, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{name}</div>
        {sub ? (
          <div className="muted mono" style={{ fontSize: 11, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{sub}</div>
        ) : role ? (
          <div className="muted" style={{ fontSize: 11 }}>{role}</div>
        ) : null}
      </div>
    </div>
  );
}

export function RelatedLinkRow({ label, to, title, hint, chip }) {
  return (
    <Link
      to={to}
      style={{
        display: 'grid',
        gridTemplateColumns: 'auto 1fr auto auto',
        gap: 14,
        padding: '14px 24px',
        borderBottom: '0.5px solid var(--border-primary)',
        alignItems: 'center',
        textDecoration: 'none',
        color: 'inherit',
      }}
    >
      {chip}
      <div style={{ minWidth: 0 }}>
        <div className="tertiary" style={{ fontSize: 10, textTransform: 'uppercase', letterSpacing: 0.6, marginBottom: 2 }}>{label}</div>
        <div style={{ fontSize: 13, fontWeight: 450, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{title}</div>
        {hint && <div className="muted mono" style={{ fontSize: 11 }}>{hint}</div>}
      </div>
      <span />
      <ChevronRight className="w-[11px] h-[11px]" style={{ opacity: 0.4 }} />
    </Link>
  );
}
